import React, { useState, useEffect } from 'react';
import useRectificationStore from '../../stores/useRectificationStore';
import styles from './ToolCard.module.css';
import DynamicSlider from '../ui/DynamicSlider';
import DeleteIcon from '../../assets/icons/delete.svg?react';

import { useToolManager } from '../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../constants/tools';
import useEditorStore from '../../stores/useEditorStore';

const SmoothningToolCard = () => {
  const smoothningState = useRectificationStore((state) => state.smoothning);
  const setSmoothning = useRectificationStore((state) => state.setSmoothning);
  const isApplied = useRectificationStore((state) => state.isSmootheningApplied);
  const undoCommand = useRectificationStore((state) => state.currentSmootheningCommand);
  const isAppliedFromDraft = useRectificationStore((state) => state.isSmootheningAppliedFromDraft);
  const { run } = useToolManager(); 
  // Use local state for UI but sync with store
  const [zones, setZones] = useState(smoothningState.zones);
  const [nextId, setNextId] = useState(zones.length > 0 ? Math.max(...zones.map(z => z.id)) + 1 : 1);

  // Sync with store when component mounts or store changes
  useEffect(() => {
    setZones(smoothningState.zones);
    if (smoothningState.zones.length > 0) {
      setNextId(Math.max(...smoothningState.zones.map(z => z.id)) + 1);
    }
  }, [smoothningState.zones]);

  const colors = [
    '#f97316', // Orange
    '#ec4899', // Pink
    '#8b5cf6', // Purple
    '#06b6d4', // Cyan
    '#10b981', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#3b82f6'  // Blue
  ];

  const addNewZone = () => {
    const newZone = {
      id: nextId,
      name: `Zone ${nextId.toString().padStart(2, '0')}`,
      smootheningValue: 1,
      isExpanded: zones.length === 0, // First zone is expanded by default
      color: colors[(nextId - 1) % colors.length]
    };
    
    const newZones = [...zones, newZone];
    setZones(newZones);
    setNextId(nextId + 1);
    
    // Update store
    setSmoothning({ zones: newZones });
  };

  const deleteZone = (id) => {
    const newZones = zones.filter(zone => zone.id !== id);
    setZones(newZones);
    
    // If we deleted the expanded zone, expand the first remaining zone
    if (newZones.length > 0 && !newZones.some(zone => zone.isExpanded)) {
      const updatedZones = newZones.map((zone, index) => 
        index === 0 ? { ...zone, isExpanded: true } : zone
      );
      setZones(updatedZones);
      setSmoothning({ zones: updatedZones });
    } else {
      setSmoothning({ zones: newZones });
    }
  };

  const toggleZone = (id) => {
    const updatedZones = zones.map(zone => ({
      ...zone,
      isExpanded: zone.id === id ? !zone.isExpanded : false
    }));
    setZones(updatedZones);
    setSmoothning({ zones: updatedZones });
  };

  const updateSmootheningValue = (id, value) => {
    const updatedZones = zones.map(zone => 
      zone.id === id ? { ...zone, smootheningValue: parseFloat(value) || 0 } : zone
    );
    setZones(updatedZones);
    setSmoothning({ zones: updatedZones });
  };

  const applySmoothening = () => {
    if(!isApplied)
      run({tool: TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_smoothning.id, mode: 'apply'});
    else if(undoCommand){
      const { resetHistoryUpTo } = useEditorStore.getState();
      resetHistoryUpTo(undoCommand);

      //call undo
      undoCommand.undo();
    }
  };

  const isLoadedFromDraft = isApplied && isAppliedFromDraft;

  return (
    <div className='flex flex-col absolute left-[10px] top-[10px]'>
      <div className='bg-white rounded-md p-2 pb-3 shadow-[0_2px_8px_rgba(0,0,0,0.15)] w-[264px] z-[1000] max-h-[70vh] overflow-y-auto border border-gray-200'>
        <div className={styles.toolCardHeader}>
          <h3>Smoothning</h3>
          {!isApplied ? (zones.length === 0 && <p>Configure smoothning zones</p>) : <p>Smoothening Applied</p>}
        </div>

        {!isApplied && zones.length > 0 && (
          <div className={styles.zonesContainer}>
            <DynamicSlider
              min={0.25}
              max={3}
              step={0.25}
              value={smoothningState.brushRadius}
              onChange={(value) => setSmoothning({ brushRadius: value })}
              label='Brush Radius'
              layout='inline'
              className='my-4 px-2'
              labelClassName='text-gray-500'
              thumbColor='#ffb900'
            />
            
            <div className='max-h-92 overflow-y-auto pr-2'>
              {zones.map((zone) => (
                <div key={zone.id} className={styles.zoneContainer}>
                  {!zone.isExpanded ? (
                    // Collapsed zone
                    <div 
                      className={styles.zoneHeaderCollapsed}
                      onClick={() => toggleZone(zone.id)}
                    >
                      <div 
                        className={styles.zoneIndicator}
                        style={{ backgroundColor: zone.color }}
                      ></div>
                      <span className={styles.zoneName}>{zone.name}</span>
                    </div>
                  ) : (
                    // Expanded zone
                    <div className={styles.zoneExpanded}>
                      <div className={styles.zoneHeaderExpanded}>
                        <div className={styles.zoneHeaderLeft}>
                          <div 
                            className={styles.zoneIndicator}
                            style={{ backgroundColor: zone.color }}
                          ></div>
                          <span className={styles.zoneName}>{zone.name}</span>
                        </div>
                        <div className={styles.zoneHeaderRight}>
                          <button 
                            className={styles.deleteButton}
                            onClick={() => deleteZone(zone.id)}
                            title="Delete zone"
                          >
                            <DeleteIcon className='w-5 h-5' />
                          </button>
                        </div>
                      </div>

                      <div className={styles.zoneContent}>
                        <div className={styles.sliderContainer}>
                          <DynamicSlider
                            min={1}
                            max={80}
                            value={zone.smootheningValue}
                            onChange={(value) => updateSmootheningValue(zone.id, value)}
                            label='Smoothning'
                            layout="inline"
                            labelClassName='font-medium'
                            activeTrackClassName='bg-gradient-to-r from-blue-500 to-blue-300'
                            valueClassName='text-violet-500 font-medium'
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.toolCardFooter}>
          {!isApplied ? (
            <button 
              className={styles.addButton}
              onClick={addNewZone}
            >
              Add New Zone
            </button>
          ) : (
            <button className={`px-2 py-1 rounded-md min-w-20 text-white ${
              isLoadedFromDraft
                ? 'bg-gray-400 cursor-not-allowed opacity-60'
                : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
            }`}
            onClick={isLoadedFromDraft ? undefined : applySmoothening}
            disabled={isLoadedFromDraft}
            title={isLoadedFromDraft ? "Cannot unapply smoothening loaded from draft" : "Unapply smoothening"}>
              Unapply
            </button>
          )}
        </div>
      </div>
      {!isApplied && zones.length > 0 && (
      <div className='flex items-center justify-between mt-4 px-2'>
        <label className="flex items-center cursor-pointer select-none ml-1">
          <input
            type="checkbox"
            checked={smoothningState.previewVisible}
            onChange={() => setSmoothning({ previewVisible: !smoothningState.previewVisible })}
            className="sr-only"
          />
          <div className={`w-10 h-6 rounded-full transition-colors duration-200 relative ${smoothningState.previewVisible ? 'bg-purple-600' : 'bg-gray-300'}`}>
            <div
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${smoothningState.previewVisible ? 'translate-x-4' : 'translate-x-0'}`}
            ></div>
          </div>
          <span className={`ml-3 text-sm font-medium ${smoothningState.previewVisible ? 'text-gray-700' : 'text-gray-700'}`}>
            Show Preview
          </span>
        </label>

        <button
          className={`px-2 py-1 rounded-md ml-2 min-w-22 text-white text-lg cursor-pointer bg-violet-600 hover:text-white hover:bg-violet-500`}
          onClick={()=> {
            applySmoothening();
          }}
        >
          Apply
        </button>
      </div>)}
    </div>
  );
};

export default SmoothningToolCard; 